import React, { useState } from 'react';
import { Classable, Dictionary, HasChildren, Shapeable } from '@shapeable/types';
import styled, { css } from 'styled-components';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames, onSubmitNetlifyForm } from '@shapeable/utils'; 
import EmailValidator from 'email-validator';
import { includes, trim, without } from 'lodash';
import ReCAPTCHA from 'react-google-recaptcha';
import { sprintf } from 'sprintf-js';
import { Button, ErrorList, Field, FormLoader, Input, InputCheckbox, MarkdownContent, MarkdownContentLinkTargetBlank, Select, SelectOption, TextArea, useForm, useLang, usePageEntity, useReady, useSiteOwnerName, useSiteRecaptchaKey, useTermsPagePath } from '@shapeable/ui';
import { DottedChevronDownIcon } from '@shapeable/icons';

const cls = classNames('contact-form');

// -------- Types -------->

export type ContactFormProps = Classable & HasChildren & {
  beforeSubmit?: () => void;
  afterSubmit?: () => void;
  types?: string[];
  typesRequired?: boolean;
  typesLabel?: string;
  typesRequiredError?: string;
  organisationTypes?: string[];
  showMembershipCheckbox?: boolean; 
  showSubscribeCheckbox?: boolean; 
}

export const ContactFormDefaultProps: ContactFormProps = {
  showMembershipCheckbox: true,
  showSubscribeCheckbox: true,
  organisationTypes: [],
  typesRequired: true,
  typesLabel: 'What would you like to contact us about? (Required)',
  typesRequiredError: 'Please select the reason you are contacting us.',
  types: [],
};

// -------- Child Component Props -------->

type ContainerProps = {

}

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
  `,
});

const FieldStyles = breakpoints({
  base: css`
    align-self: stretch;
    label {
      color: ${theme.COLOR('text')};
    }
  `,
});

const FormStyles = breakpoints({
  base: css`
    padding-bottom: ${theme.UNIT(3)};
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
});

const BaseInputStyles = breakpoints({
  base: css`
    &:focus {
      outline: none;
      border: 2px dotted ${theme.COLOR('link-hover')};
    }

    ${({ hasError }: any ) => hasError && css`
      outline: none;
      border-color: red;
    `}
  `,
});



const TextAreaStyles = breakpoints({
  base: css`
    width: 100%;
    height: 150px;
    padding: ${theme.UNIT(4)};
    ${BaseInputStyles};
  `,
});

const SelectStyles = breakpoints({
  base: css`
    width: 100%;
    font-size: ${theme.FONT_SIZE(14)};

    .shp--select__control {
      border-width: 2px;
      border-style: dotted;
      
      &:hover {
        border-color: ${theme.COLOR('line-mid')};
      }
    }
  `,
});

const InputStyles = breakpoints({
  base: css`
    width: 100%;
    padding: ${theme.UNIT(4)};
    color: ${theme.COLOR('primary')};
    ${BaseInputStyles};
  `,
});

const SubmitButtonStyles = breakpoints({
  base: css`  
    width: 200px;
    margin-top: ${theme.UNIT(8)};
  `,
});


const LoaderStyles = breakpoints({
  base: css`
    background: none;
    padding-bottom: ${theme.UNIT(100)};
  `,
});

const ThankyouStyles = breakpoints({
  base: css`
    padding: ${theme.UNIT(2)};
    width: 100%;
    box-sizing: border-box;
    flex-grow: 1;
    color: ${theme.COLOR('text')};
    font-weight: 400;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: column;
    text-align: center;
    p {
      margin: ${theme.UNIT(2)};
    }
  `,
});

const ThankyouMessageStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex-grow: 1;
    font-size: 1.25em;
  `,
});


const IndicatorStyles = breakpoints({
  base: css`
    width: 20px;
    margin: 0 16px;
  `,
});

const InputCheckboxStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('text')};
    margin: ${theme.UNIT(1)} 0;
    align-self: stretch;
  `,
});

const UpdatesStyles = breakpoints({
  base: css`
    ${InputCheckboxStyles};
    margin-bottom: ${theme.UNIT(8)};
  `,
});

const ErrorsStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('red')};
    padding: ${theme.UNIT(6)} ${theme.UNIT(6)} ${theme.UNIT(7)};
    margin-top: ${theme.UNIT(4)};
    margin-bottom: ${theme.UNIT(4)};
    font-weight: 500;
    border: 2px dotted ${theme.COLOR('red')};
    font-size: ${theme.FONT_SIZE(14)};

    ul {
      padding-left: ${theme.UNIT(4)};
    }

    li {
      margin: ${theme.UNIT(2)} 0; 
      padding-left: 0;
    }
  `,
});

// -------- Components -------->

const MESSAGE = 'details';
const GIVEN_NAME = 'givenName';
const FAMILY_NAME = 'familyName';
const EMAIL = 'email';
const COLLABORATION_TYPE = 'collaborationType';
const COLLABORATION_AREA = 'collaborationArea';
const FORM_NAME = 'contact';
const AGREE = 'agree';
const BECOME_MEMBER = 'becomeAMember';
const RECEIVE_UPDATES = 'receiveUpdates';

const COLLABORATION_TYPE_OPTIONS: SelectOption[] = [
  { label: 'Money', value: 'Money' },
  { label: 'Time', value: 'Time' },
  { label: 'People', value: 'People' },
  { label: 'Partnership', value: 'Partnership' },
];

const COLLABORATION_AREA_OPTIONS: SelectOption[] = [
  { label: 'Nature Repair Fund', value: 'Nature Repair Fund' },
  { label: 'Design', value: 'Design' },
  { label: 'Consultation', value: 'Consultation' },
];


const My = {
  Container: styled.div<ContainerProps>`${ContainerStyles}`,
    Loader: styled(FormLoader)`${LoaderStyles}`,
    Thankyou: styled.div`${ThankyouStyles}`,
      ThankyouMessage: styled.div`${ThankyouMessageStyles}`,

    Agree: styled(InputCheckbox).attrs({ id: AGREE, name: AGREE })`${InputCheckboxStyles}`,
    BecomeAMember: styled(InputCheckbox).attrs({ id: BECOME_MEMBER })`${InputCheckboxStyles}`,
    Updates: styled(InputCheckbox).attrs({ id: RECEIVE_UPDATES })`${UpdatesStyles}`,
    
    Indicator: styled(DottedChevronDownIcon)`${IndicatorStyles}`,

    Form: styled.form`${FormStyles}`,

      CollaborationTypeField: styled(Field).attrs({ id: COLLABORATION_TYPE })`${FieldStyles}`,
        CollaborationType: styled(Select).attrs({ id: COLLABORATION_TYPE, placeholder: 'Select' })`${SelectStyles}`,
      CollaborationAreaField: styled(Field).attrs({ id: COLLABORATION_AREA })`${FieldStyles}`,
        CollaborationArea: styled(Select).attrs({ id: COLLABORATION_AREA, placeholder: 'Select' })`${SelectStyles}`,
      GivenNameField: styled(Field).attrs({ id: GIVEN_NAME })`${FieldStyles}`,
        GivenName: styled(Input).attrs({ id: GIVEN_NAME, placeholder: '' })`${InputStyles}`,
      FamilyNameField: styled(Field).attrs({ id: FAMILY_NAME })`${FieldStyles}`,
        FamilyName: styled(Input).attrs({ id: FAMILY_NAME, placeholder: '' })`${InputStyles}`,
      MessageField: styled(Field).attrs({ id: MESSAGE })`${FieldStyles}`,
        Message: styled(TextArea).attrs({ id: MESSAGE })`${TextAreaStyles}`,
      EmailField: styled(Field).attrs({ id: EMAIL })`${FieldStyles}`,
        Email: styled(Input).attrs({ id: EMAIL, placeholder: '' })`${InputStyles}`,

      Errors: styled(ErrorList)`${ErrorsStyles}`,

    SubmitButton: styled(Button)`${SubmitButtonStyles}`,
};
  
export const ContactForm: Shapeable.FC<ContactFormProps> = (props) => {
  const { className, showMembershipCheckbox, showSubscribeCheckbox } = props;
  const form = useForm(FORM_NAME);

  const { values, patchValues } = form;
  const [ token, setToken ] = useState('');
  const [ errors, setErrors ] = useState<string[]>([]);
  const [ errorFields, setErrorFields ] = useState<string[]>([]);

  const t = useLang();

  const page = usePageEntity();
  
  const formState: Dictionary<any> = { 
    ...values, 
  };

  const SITE_RECAPTCHA_KEY = useSiteRecaptchaKey();
  const TERMS_PAGE_PATH = useTermsPagePath();
  const SITE_OWNER_NAME = useSiteOwnerName();

  const onChange = (name: string) => (value: string | SelectOption) => { patchValues({ [name]: value }); setErrors([]); setErrorFields(without(errorFields, name)); };

  const onSubmit = async(event: React.FormEvent) => {
    event.preventDefault();

    const e = [];
    const ef = [];
    
    if (!token && SITE_RECAPTCHA_KEY) {
      e.push(t("Please check the I'm not a robot checkbox to validate you are human"));
    }
    
    if (trim(formState[GIVEN_NAME]) === '') {
      e.push(t('Please enter your first name.'));
      ef.push(GIVEN_NAME);
    }

    if (trim(formState[FAMILY_NAME]) === '') {
      e.push(t('Please enter your last name.'));
      ef.push(FAMILY_NAME);
    }

    if (trim(formState[MESSAGE]) === '') {
      e.push(t("Please provide the details of your request in the Message field."));
      ef.push(MESSAGE);
    }

    if (trim(formState[EMAIL]) === '') {
      e.push(t("Please enter your email address."));
      ef.push(EMAIL);
    } else if (!EmailValidator.validate(formState[EMAIL])) {
      e.push(t("Please check that your email address is VALID"));
      ef.push(EMAIL);
    }

    if (trim(formState[AGREE]) === '') {
      e.push(t('Please confirm you agree with the terms of Regen Studios.'));
      ef.push(AGREE);
    }

    setErrorFields(ef);
    setErrors(e);

    if (e.length) {
      return false;
    }

    const data = { ...formState, 'g-recaptcha-response': token };

    if (props.beforeSubmit) {
      props.beforeSubmit();
    };

    form.startSubmit();
    const response = await onSubmitNetlifyForm(FORM_NAME, page.path, data);
    // console.log("FORM DATA", data);
    
    setToken('');
    form.endSubmit();

    if (props.afterSubmit) {
      props.afterSubmit();
    };

    return false;

  };

  const onCaptchaChange = (token: any) => {
    setToken(token);
  };

  const isSubmitDisabled = !!((!token && SITE_RECAPTCHA_KEY) || form.isSubmitting);
  const hasErrors = !!errors.length;
  const { isReady } = useReady();

  const agreeText = TERMS_PAGE_PATH ?
    sprintf(t('I confirm that I have read and agree to the [terms](%s) of Regen Studios.'), TERMS_PAGE_PATH) :
    t('I confirm that I have read and agree to the terms of Regen Studios.');

  return (
    <My.Container className={className}>
      {
        (form.isSubmitting) && 
        <My.Loader color="primary">{t("Please wait...")}</My.Loader>
      }
      {
        !form.isSubmitting && (
        (form.isSubmitted) ?
        <My.Thankyou>
          <My.ThankyouMessage>
          <p>
          {t("Thankyou, your submission has been received.")}
          </p>
          <p>
          {t('We will be in touch soon.')}
          </p>
          </My.ThankyouMessage>
        </My.Thankyou> :
        <My.Form name={FORM_NAME} onSubmit={onSubmit} method="post" data-netlify-recaptcha="true" data-netlify="true">
          <input type="hidden" name="form-name" value={FORM_NAME} />

          <My.GivenNameField label={t('First Name (Required):')}>
            <My.GivenName hasError={includes(errorFields, GIVEN_NAME)} onChange={onChange(GIVEN_NAME)} value={formState[GIVEN_NAME] || ''} />
          </My.GivenNameField>

          <My.FamilyNameField label={t('Last Name (Required):')}>
            <My.FamilyName hasError={includes(errorFields, FAMILY_NAME)} onChange={onChange(FAMILY_NAME)} value={formState[FAMILY_NAME] || ''} />
          </My.FamilyNameField>

          <My.EmailField label={t('Email (Required):')}>
            <My.Email hasError={includes(errorFields, EMAIL)} onChange={onChange(EMAIL)} value={formState[EMAIL] || ''} />
          </My.EmailField>

          <My.CollaborationTypeField label={t('How would you like to collaborate with us?')}>
            <My.CollaborationType
              hasError={includes(errorFields, COLLABORATION_TYPE)}
              onChange={onChange(COLLABORATION_TYPE)}
              options={COLLABORATION_TYPE_OPTIONS}
              value={formState[COLLABORATION_TYPE] || ''}
              borderStyle="dotted"
            />
          </My.CollaborationTypeField>

          <My.CollaborationAreaField label={t('Which area does your collaboration best fit with?')}>
            <My.CollaborationArea
              hasError={includes(errorFields, COLLABORATION_AREA)}
              onChange={onChange(COLLABORATION_AREA)}
              options={COLLABORATION_AREA_OPTIONS}
              value={formState[COLLABORATION_AREA] || ''}
              borderStyle="dotted"
            />
          </My.CollaborationAreaField>

          <My.MessageField label={t('Message (Required):')}>
            <My.Message hasError={includes(errorFields, MESSAGE)} onChange={onChange(MESSAGE)}>{formState[MESSAGE] || ''}</My.Message>
          </My.MessageField>

          <My.Agree onChange={onChange(AGREE)} valid={!includes(errorFields, AGREE)} checked={formState[AGREE] === 'yes'}>
            <MarkdownContent 
              text={agreeText} 
              embedComponents={{
                a: {
                  component: MarkdownContentLinkTargetBlank
                }
              }}
            />
          </My.Agree>

          {
            showMembershipCheckbox &&
            <My.BecomeAMember label={sprintf(t('I would like to become a %s member'), SITE_OWNER_NAME)} onChange={onChange(BECOME_MEMBER)} checked={formState[BECOME_MEMBER] === 'yes'} />
          }
          {
            showSubscribeCheckbox &&
            <My.Updates label={sprintf(t(`I\'d like to receive updates from %s`), SITE_OWNER_NAME)} name="agree" onChange={onChange(RECEIVE_UPDATES)} checked={formState[RECEIVE_UPDATES] === 'yes'} />
          }
          {
            SITE_RECAPTCHA_KEY && 
            <ReCAPTCHA
              sitekey={SITE_RECAPTCHA_KEY}
              onChange={onCaptchaChange}
            />
          }
          {
            hasErrors && 
            <My.Errors items={errors}>
            {t('Sorry, we need a bit more information:')}
            </My.Errors>
          }
          <My.SubmitButton type="submit" disabled={isSubmitDisabled}>{t('Submit')}</My.SubmitButton>
        </My.Form>
        )
      } 
      
    </My.Container>
  )
};

ContactForm.defaultProps = ContactFormDefaultProps;
ContactForm.cls = cls;
